// frontend/src/pages/superAdmin/ManageSystemSettings.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';
import Footer from '../../components/Footer';
import Swal from 'sweetalert2';
import api from '../../services/api';

const ManageSystemSettings = ({ user, setUser }) => {
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [showScores, setShowScores] = useState(false);
  const [formData, setFormData] = useState({
    iit_start_date: '',
    iit_end_date: '',
    eit1_start_date: '',
    eit1_end_date: '',
    eit2_start_date: '',
    eit2_end_date: '',
    oit_start_date: '',
    oit_end_date: '',
    evaluation_start_date: '',
    evaluation_end_date: '',
    show_scores: false
  });

  useEffect(() => {
    const fetchFiscalYears = async () => {
      try {
        const response = await api.get('/fiscal-years');
        setFiscalYears(response.data);
        
        // ถ้ามีปีงบประมาณที่เปิดใช้งานอยู่ ให้เลือกปีนั้น
        const activeFiscalYear = response.data.find(year => year.active);
        if (activeFiscalYear) {
          setSelectedFiscalYear(activeFiscalYear.id);
          fetchSettings(activeFiscalYear.id);
        }
      } catch (error) {
        console.error('Error fetching fiscal years:', error);
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถดึงข้อมูลปีงบประมาณได้',
          confirmButtonText: 'ตกลง'
        });
      }
    };

    fetchFiscalYears();
  }, []);

  const fetchSettings = async (fiscalYearId) => {
    if (!fiscalYearId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/system-settings/${fiscalYearId}`);
      setSettings(response.data);
      setShowScores(response.data.show_scores || false);
      
      // แปลงวันที่ ISO เป็นรูปแบบที่ใช้ในฟอร์ม (YYYY-MM-DDTHH:MM)
      const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toISOString().slice(0, 16);
      };
      
      setFormData({
        iit_start_date: formatDate(response.data.iit_start_date),
        iit_end_date: formatDate(response.data.iit_end_date),
        eit1_start_date: formatDate(response.data.eit1_start_date),
        eit1_end_date: formatDate(response.data.eit1_end_date),
        eit2_start_date: formatDate(response.data.eit2_start_date),
        eit2_end_date: formatDate(response.data.eit2_end_date),
        oit_start_date: formatDate(response.data.oit_start_date),
        oit_end_date: formatDate(response.data.oit_end_date),
        evaluation_start_date: formatDate(response.data.evaluation_start_date),
        evaluation_end_date: formatDate(response.data.evaluation_end_date),
        show_scores: response.data.show_scores || false
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      // ถ้าเป็น 404 แสดงว่ายังไม่มีการตั้งค่าสำหรับปีงบประมาณนี้
      if (error.response && error.response.status === 404) {
        setSettings(null);
        resetForm();
      } else {
        Swal.fire({
          icon: 'error',
          title: 'เกิดข้อผิดพลาด',
          text: 'ไม่สามารถดึงข้อมูลการตั้งค่าระบบได้',
          confirmButtonText: 'ตกลง'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      iit_start_date: '',
      iit_end_date: '',
      eit1_start_date: '',
      eit1_end_date: '',
      eit2_start_date: '',
      eit2_end_date: '',
      oit_start_date: '',
      oit_end_date: '',
      evaluation_start_date: '',
      evaluation_end_date: '',
      show_scores: false
    });
  };

  const handleFiscalYearChange = (e) => {
    const value = e.target.value;
    setSelectedFiscalYear(value);
    fetchSettings(value);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // ตรวจสอบวันที่
    const checkDates = () => {
      const dateValidations = [
        { start: formData.iit_start_date, end: formData.iit_end_date, name: 'IIT' },
        { start: formData.eit1_start_date, end: formData.eit1_end_date, name: 'EIT1' },
        { start: formData.eit2_start_date, end: formData.eit2_end_date, name: 'EIT2' },
        { start: formData.oit_start_date, end: formData.oit_end_date, name: 'OIT' },
        { start: formData.evaluation_start_date, end: formData.evaluation_end_date, name: 'การประเมิน OIT' }
      ];
      
      for (const validation of dateValidations) {
        if (validation.start && validation.end) {
          const startDate = new Date(validation.start);
          const endDate = new Date(validation.end);
          
          if (startDate >= endDate) {
            Swal.fire({
              icon: 'error',
              title: 'ข้อมูลไม่ถูกต้อง',
              text: `วันที่เริ่มต้นของ ${validation.name} ต้องน้อยกว่าวันที่สิ้นสุด`,
              confirmButtonText: 'ตกลง'
            });
            return false;
          }
        }
      }
      
      return true;
    };
    
    if (!checkDates()) {
      return;
    }
    
    setFormSubmitting(true);
    
    try {
      if (settings) {
        // อัปเดตการตั้งค่าที่มีอยู่
        await api.put(`/system-settings/${settings.id}`, formData);
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'อัปเดตการตั้งค่าระบบเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      } else {
        // สร้างการตั้งค่าใหม่
        await api.post('/system-settings', {
          ...formData,
          fiscal_year_id: selectedFiscalYear
        });
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: 'สร้างการตั้งค่าระบบเรียบร้อยแล้ว',
          confirmButtonText: 'ตกลง'
        });
      }
      
      // โหลดข้อมูลใหม่
      fetchSettings(selectedFiscalYear);
    } catch (error) {
      console.error('Error saving settings:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถบันทึกการตั้งค่าระบบได้',
        confirmButtonText: 'ตกลง'
      });
    } finally {
      setFormSubmitting(false);
    }
  };

  // ฟังก์ชันสำหรับเปิด/ปิดการแสดงผลคะแนน
  const handleToggleShowScores = async () => {
    try {
      if (!settings) {
        return Swal.fire({
          icon: 'error',
          title: 'ไม่พบการตั้งค่าระบบ',
          text: 'กรุณาสร้างการตั้งค่าระบบก่อน',
          confirmButtonText: 'ตกลง'
        });
      }

      const result = await Swal.fire({
        icon: 'question',
        title: `${showScores ? 'ปิด' : 'เปิด'}การแสดงผลคะแนน?`,
        text: `คุณต้องการ${showScores ? 'ปิด' : 'เปิด'}การแสดงผลคะแนนการประเมินใช่หรือไม่?`,
        showCancelButton: true,
        confirmButtonText: 'ใช่',
        cancelButtonText: 'ยกเลิก'
      });

      if (result.isConfirmed) {
        const response = await api.put(`/system-settings/${settings.id}/toggle-show-scores`, {
          show_scores: !showScores
        });
        
        setShowScores(!showScores);
        
        // อัปเดต formData ด้วย
        setFormData({
          ...formData,
          show_scores: !showScores
        });
        
        Swal.fire({
          icon: 'success',
          title: 'สำเร็จ',
          text: response.data.message,
          confirmButtonText: 'ตกลง'
        });
      }
    } catch (error) {
      console.error('Error toggling show scores:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: error.response?.data?.message || 'ไม่สามารถอัปเดตการตั้งค่าได้',
        confirmButtonText: 'ตกลง'
      });
    }
  };

  return (
    <div className="wrapper">
      <Header user={user} title="ตั้งค่าระบบ" />
      <Sidebar user={user} />

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0">ตั้งค่าระบบ</h1>
              </div>
              <div className="col-sm-6">
                <ol className="breadcrumb float-sm-end">
                  <li className="breadcrumb-item">
                    <Link to="/super-admin/dashboard">หน้าหลัก</Link>
                  </li>
                  <li className="breadcrumb-item active">ตั้งค่าระบบ</li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <section className="content">
          <div className="container-fluid">
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">ตั้งค่าระบบ</h3>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="fiscalYear" className="form-label">เลือกปีงบประมาณ</label>
                    <select
                      id="fiscalYear"
                      className="form-select"
                      value={selectedFiscalYear}
                      onChange={handleFiscalYearChange}
                      disabled={loading}
                    >
                      <option value="">-- เลือกปีงบประมาณ --</option>
                      {fiscalYears.map((year) => (
                        <option key={year.id} value={year.id}>
                          {year.year} {year.active ? '(ปัจจุบัน)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center my-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">กำลังโหลด...</span>
                    </div>
                    <p className="mt-2">กำลังโหลดข้อมูล...</p>
                  </div>
                ) : (
                  <>
                    {selectedFiscalYear && (
                      <>
                        {/* ส่วนเปิด/ปิดการแสดงผลคะแนน */}
                        <div className="row mb-4">
                          <div className="col-md-12">
                            <div className="card">
                              <div className="card-header bg-primary text-white">
                                <h5 className="mb-0">การแสดงผลคะแนนการประเมิน</h5>
                              </div>
                              <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h5>สถานะการแสดงผลคะแนน:</h5>
                                    <p className={`badge ${showScores ? 'bg-success' : 'bg-danger'} fs-6`}>
                                      {showScores ? 'เปิด' : 'ปิด'}
                                    </p>
                                    <p className="text-muted mt-2">
                                      {showScores 
                                        ? 'ขณะนี้ผู้ใช้งานทั้งหมดสามารถมองเห็นผลคะแนนการประเมินได้' 
                                        : 'ขณะนี้ผู้ใช้งานไม่สามารถมองเห็นผลคะแนนการประเมิน ยกเว้นผู้ดูแลระบบ'}
                                    </p>
                                  </div>
                                  <button 
                                    className={`btn ${showScores ? 'btn-danger' : 'btn-success'}`}
                                    onClick={handleToggleShowScores}
                                    disabled={!settings}
                                  >
                                    <i className={`fas ${showScores ? 'fa-eye-slash' : 'fa-eye'} me-2`}></i>
                                    {showScores ? 'ปิดการแสดงผลคะแนน' : 'เปิดการแสดงผลคะแนน'}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* ฟอร์มการตั้งค่า */}
                        <form onSubmit={handleSubmit}>
                          <div className="card">
                            <div className="card-header bg-primary text-white">
                              <h5 className="mb-0">ตั้งค่าระยะเวลาการประเมิน</h5>
                            </div>
                            <div className="card-body">
                              <div className="row mb-3">
                                <div className="col-md-12">
                                  <h5>ตั้งค่าระยะเวลาการตอบแบบสอบถาม IIT (บุคลากรภายในหน่วยงาน)</h5>
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="iit_start_date" className="form-label">วันที่เริ่มต้น</label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="iit_start_date"
                                    name="iit_start_date"
                                    value={formData.iit_start_date}
                                    onChange={handleInputChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="iit_end_date" className="form-label">วันที่สิ้นสุด</label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="iit_end_date"
                                    name="iit_end_date"
                                    value={formData.iit_end_date}
                                    onChange={handleInputChange}
                                  />
                                </div>
                              </div>

                              <div className="row mb-3">
                                <div className="col-md-12">
                                  <h5>ตั้งค่าระยะเวลาการตอบแบบสอบถาม EIT1 (ผู้มีส่วนได้ส่วนเสียโรงเรียนกำหนด)</h5>
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="eit1_start_date" className="form-label">วันที่เริ่มต้น</label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="eit1_start_date"
                                    name="eit1_start_date"
                                    value={formData.eit1_start_date}
                                    onChange={handleInputChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="eit1_end_date" className="form-label">วันที่สิ้นสุด</label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="eit1_end_date"
                                    name="eit1_end_date"
                                    value={formData.eit1_end_date}
                                    onChange={handleInputChange}
                                  />
                                </div>
                              </div>

                              <div className="row mb-3">
                                <div className="col-md-12">
                                  <h5>ตั้งค่าระยะเวลาการตอบแบบสอบถาม EIT2 (ผู้มีส่วนได้ส่วนเสียทั่วไป)</h5>
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="eit2_start_date" className="form-label">วันที่เริ่มต้น</label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="eit2_start_date"
                                    name="eit2_start_date"
                                    value={formData.eit2_start_date}
                                    onChange={handleInputChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="eit2_end_date" className="form-label">วันที่สิ้นสุด</label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="eit2_end_date"
                                    name="eit2_end_date"
                                    value={formData.eit2_end_date}
                                    onChange={handleInputChange}
                                  />
                                </div>
                              </div>

                              <div className="row mb-3">
                                <div className="col-md-12">
                                  <h5>ตั้งค่าระยะเวลาการส่งข้อมูล OIT (การเปิดเผยข้อมูลสาธารณะ)</h5>
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="oit_start_date" className="form-label">วันที่เริ่มต้น</label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="oit_start_date"
                                    name="oit_start_date"
                                    value={formData.oit_start_date}
                                    onChange={handleInputChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="oit_end_date" className="form-label">วันที่สิ้นสุด</label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="oit_end_date"
                                    name="oit_end_date"
                                    value={formData.oit_end_date}
                                    onChange={handleInputChange}
                                  />
                                </div>
                              </div>

                              <div className="row mb-3">
                                <div className="col-md-12">
                                  <h5>ตั้งค่าระยะเวลาการประเมิน OIT (สำหรับกรรมการประเมิน)</h5>
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="evaluation_start_date" className="form-label">วันที่เริ่มต้น</label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="evaluation_start_date"
                                    name="evaluation_start_date"
                                    value={formData.evaluation_start_date}
                                    onChange={handleInputChange}
                                  />
                                </div>
                                <div className="col-md-6 mb-3">
                                  <label htmlFor="evaluation_end_date" className="form-label">วันที่สิ้นสุด</label>
                                  <input
                                    type="datetime-local"
                                    className="form-control"
                                    id="evaluation_end_date"
                                    name="evaluation_end_date"
                                    value={formData.evaluation_end_date}
                                    onChange={handleInputChange}
                                  />
                                </div>
                              </div>
                            </div>
                            <div className="card-footer">
                              <button 
                                type="submit" 
                                className="btn btn-primary"
                                disabled={formSubmitting || !selectedFiscalYear}
                              >
                                {formSubmitting ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    กำลังบันทึก...
                                  </>
                                ) : (
                                  <>
                                    <i className="fas fa-save me-2"></i>
                                    {settings ? 'บันทึกการเปลี่ยนแปลง' : 'สร้างการตั้งค่าระบบ'}
                                  </>
                                )}
                              </button>
                            </div>
                          </div>
                        </form>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default ManageSystemSettings;